<script setup lang="ts">
import { computed } from 'vue';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import { useBrowserDownload } from '@peanuds/composables';
import { DownloadIcon, UiButton, UiCopyToggleButton } from '@peanuds/ui';
import { useTranslation } from 'i18next-vue';

import type { Card, CardTransaction } from '../../internal/cards.types';
import { maskCardNumber } from '../../internal/utils/card-number';
import { transactionStatusTag } from '../../internal/utils/card-status';
import {
  formatCardDateTime,
  formatSignedAmount,
  formatWeekdayDate,
} from '../../internal/utils/format';
import { buildStatement } from '../../internal/utils/statement-export';
import CardDrawerShell from './CardDrawerShell.vue';

const { card, transaction } = defineProps<{ card: Card; transaction: CardTransaction }>();

const visible = defineModel<boolean>('visible', { required: true });

const { t } = useTranslation();
const toast = useToast();
const { triggerBrowserDownload } = useBrowserDownload();

const statusTag = computed(() => transactionStatusTag(transaction.status));

const rows = computed(() => [
  {
    key: 'counterparty',
    label: t(`cards.transaction_details.counterparty.${transaction.direction}`),
    value: transaction.merchant.name,
  },
  {
    key: 'date',
    label: t('cards.transaction_details.date'),
    value: formatCardDateTime(transaction.occurredAt),
  },
  { key: 'id', label: t('cards.transaction_details.id'), value: transaction.id },
]);

const downloadStatement = () => {
  triggerBrowserDownload(
    buildStatement(card, [transaction], 'pdf'),
    `${transaction.id.toLowerCase()}-statement.pdf`,
  );
  toast.add({ severity: 'success', summary: t('cards.export.done'), life: 4000 });
};
</script>

<template>
  <CardDrawerShell
    v-model:visible="visible"
    :title="t('cards.transaction_details.title')"
    test-id="card-transaction-drawer"
  >
    <div class="transaction-drawer__summary">
      <Tag
        severity="info"
        :icon="transaction.direction === 'in' ? 'pi pi-arrow-down-left' : 'pi pi-arrow-up-right'"
        :value="t(`cards.transaction_details.type.${transaction.direction}`)"
      />
      <span
        class="transaction-drawer__amount"
        :class="{ 'transaction-drawer__amount--failed': transaction.status === 'failed' }"
        data-testid="card-transaction-amount"
      >
        {{ formatSignedAmount(transaction.amount, transaction.direction, card.currency) }}
      </span>
      <span class="transaction-drawer__date">{{ formatWeekdayDate(transaction.occurredAt) }}</span>
    </div>

    <dl class="transaction-drawer__list">
      <div class="transaction-drawer__row transaction-drawer__row--inline">
        <dt class="transaction-drawer__label">{{ t('cards.transaction_details.status') }}</dt>
        <dd class="transaction-drawer__value">
          <Tag :severity="statusTag.severity" :value="t(statusTag.labelKey)" />
        </dd>
      </div>
      <div class="transaction-drawer__row">
        <dt class="transaction-drawer__label">{{ t('cards.transaction_details.card') }}</dt>
        <dd class="transaction-drawer__value transaction-drawer__value--start">
          <Tag severity="secondary" :value="card.name" />
          <span>{{ maskCardNumber(card.last4) }}</span>
        </dd>
      </div>
      <div v-for="row in rows" :key="row.key" class="transaction-drawer__row">
        <dt class="transaction-drawer__label">{{ row.label }}</dt>
        <dd class="transaction-drawer__value">
          <span>{{ row.value }}</span>
          <UiCopyToggleButton
            class="transaction-drawer__copy"
            :copy-text="row.value"
            :tooltip-text="t('cards.drawer.about.copy')"
            :tooltip-success-text="t('cards.drawer.about.copied')"
            button-size="2rem"
            icon-dark
          />
        </dd>
      </div>
    </dl>

    <template #footer>
      <UiButton
        class="transaction-drawer__download"
        :label="t('cards.transaction_details.download')"
        data-testid="card-transaction-download"
        @click="downloadStatement"
      >
        <template #icon>
          <DownloadIcon />
        </template>
      </UiButton>
    </template>
  </CardDrawerShell>
</template>

<style scoped lang="scss">
.transaction-drawer {
  &__summary {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-s;
    padding-bottom: $spacing-md;
  }

  &__amount {
    font-size: $font-size-2xl;
    font-weight: $font-weight-medium;
    line-height: $line-height-xl;

    &--failed {
      color: $color-state-danger;
      text-decoration: line-through;
    }
  }

  &__date {
    color: $color-text-secondary;
    font-size: $font-size-sm;
    line-height: $line-height-sm;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: $spacing-md;
    margin: 0;
  }

  &__row {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;

    &--inline {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  &__label {
    color: $color-text-secondary;
    font-size: $font-size-sm;
    line-height: $line-height-sm;
  }

  &__value {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-s;
    margin: 0;
    font-size: $font-size-md;
    font-weight: $font-weight-medium;
    line-height: $line-height-sm;

    &--start {
      justify-content: flex-start;
    }
  }

  &__copy {
    flex-shrink: 0;
  }

  &__download {
    width: 100%;
  }
}
</style>
